/**
 * QR コード一括 PDF 生成ユーティリティ
 *
 * 全ユーザー分の QR ログイン deepLink を 1 ページ 1 名の PDF にまとめてダウンロードする。
 *
 * 日本語（氏名・メール等）は jsPDF 標準フォントでは描画できないため、既存の
 * pdfGenerator.ts と同じく「HTML をブラウザのフォントで描画 → html2canvas で画像化 →
 * jsPDF に貼り付け」方式を採用する。QR 画像は qrcode ライブラリで PNG dataURL を生成する。
 */
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import type { BulkIssueTokenItem } from '../api/qrAuthApi';

/** A4 縦 96dpi 相当のピクセルサイズ（html2canvas のレンダリング元サイズ） */
const A4_PORTRAIT_WIDTH_PX = 794;
const A4_PORTRAIT_HEIGHT_PX = 1123;
/** A4 縦の mm サイズ */
const A4_PORTRAIT_WIDTH_MM = 210;
const A4_PORTRAIT_HEIGHT_MM = 297;

/**
 * 有効期限（ISO8601）を日本語ロケールの日時表記へ整形する。
 * QrIssuancePage の formatExpiry と同方針。
 */
function formatExpiry(expiresAt: string): string {
  return new Date(expiresAt).toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** HTML 特殊文字をエスケープする（ユーザー入力をそのまま innerHTML に流さない）。 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * 1 ユーザー分のページ要素（A4 縦）を生成する。
 * @param item ユーザー情報＋deepLink＋有効期限
 * @param qrDataUrl 事前生成した QR の PNG dataURL
 */
function buildPageElement(item: BulkIssueTokenItem, qrDataUrl: string): HTMLElement {
  const page = document.createElement('div');
  page.style.width = `${A4_PORTRAIT_WIDTH_PX}px`;
  page.style.height = `${A4_PORTRAIT_HEIGHT_PX}px`;
  page.style.boxSizing = 'border-box';
  page.style.padding = '64px 56px';
  page.style.background = '#ffffff';
  page.style.display = 'flex';
  page.style.flexDirection = 'column';
  page.style.alignItems = 'center';
  page.style.justifyContent = 'center';
  page.style.fontFamily =
    '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Yu Gothic", Meiryo, sans-serif';
  page.style.color = '#1a1a1a';

  // 会社名・部署名（存在するもののみ表示）。氏名の上にヘッダーとして配置する。
  const orgLines = [item.companyName, item.departmentName]
    .filter((v) => v && v.trim() !== '')
    .map(
      (v, i) =>
        `<div style="font-size:${i === 0 ? 26 : 22}px;${i === 0 ? 'font-weight:700;' : 'color:#555;'}text-align:center;line-height:1.3;">${escapeHtml(v)}</div>`
    )
    .join('');

  page.innerHTML = `
    ${orgLines ? `<div style="margin-bottom:24px;">${orgLines}</div>` : ''}
    <div style="font-size:40px;font-weight:700;text-align:center;margin-bottom:40px;line-height:1.3;">
      ${escapeHtml(item.name)}
    </div>
    <!-- ユーザー名(メール形式)・メールアドレスはPDFに表示しない（要望により非表示） -->
    <img src="${qrDataUrl}" alt="QR" width="480" height="480"
         style="display:block;width:480px;height:480px;" />
    <div style="font-size:20px;color:#555;text-align:center;margin-top:40px;">
      有効期限：${escapeHtml(formatExpiry(item.expiresAt))}
    </div>
  `;
  return page;
}

/**
 * 全ユーザー分の QR を 1 ページ 1 名の PDF に出力してダウンロードする。
 *
 * @param items 一括発行 API が返したユーザーごとの結果
 * @param filename ダウンロードファイル名
 * @param onProgress 進捗コールバック（生成済みページ数 / 総数）
 */
export async function generateBulkQrPdf(
  items: BulkIssueTokenItem[],
  filename = 'qr-codes-all-users.pdf',
  onProgress?: (done: number, total: number) => void
): Promise<void> {
  if (items.length === 0) {
    throw new Error('出力対象のユーザーがいません');
  }

  // html2canvas が描画できるよう、画面外のコンテナを用意する。
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  try {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // QR は deepLink のみをエンコードする（生トークンは deepLink 内）。
      const qrDataUrl = await QRCode.toDataURL(item.deepLink, {
        width: 480,
        margin: 1,
        errorCorrectionLevel: 'M',
      });

      const page = buildPageElement(item, qrDataUrl);
      container.appendChild(page);

      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      container.removeChild(page);

      if (i > 0) {
        pdf.addPage();
      }
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(imgData, 'JPEG', 0, 0, A4_PORTRAIT_WIDTH_MM, A4_PORTRAIT_HEIGHT_MM);

      onProgress?.(i + 1, items.length);
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
}
